import { Link } from "react-router-dom";
import { ArrowRight, Brush, Clock, Phone, Ruler, ShieldCheck } from "lucide-react";
import { COMPANY, STATS } from "@/data/site";
import { Overline, Reveal } from "@/components/Reveal";
import EstimateForm from "@/components/EstimateForm";

export const StatsRow = () => (
  <section className="border-b border-white/10 bg-navy-deep" data-testid="stats-row">
    <div className="mx-auto grid max-w-7xl grid-cols-2 divide-white/10 px-6 sm:grid-cols-4 sm:divide-x lg:px-10">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08} className="px-4 py-8 text-center sm:py-10">
          <p className="font-serif text-3xl font-bold text-salmon-vibrant sm:text-4xl">{s.value}</p>
          <p className="mt-1 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const WHY = [
  { icon: ShieldCheck, title: "Licensed & Insured", text: "Full liability and workers' comp on every job — certificates on request." },
  { icon: Ruler, title: "Master Tile Craftsmanship", text: "Fifteen years of showers, floors and backsplashes set perfectly level." },
  { icon: Brush, title: "Clean-Home Guarantee", text: "Dust barriers, floor protection and a spotless cleanup, every day." },
  { icon: Clock, title: "On Schedule, On Budget", text: "Written line-item quotes and a schedule we actually keep." },
];

export const WhyUs = ({ title = "Why Huntsville Homeowners Choose Gregory Tile" }) => (
  <section className="bg-white py-16 md:py-24" data-testid="why-us-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Overline>The Gregory Tile Difference</Overline>
        <h2 className="mt-3 max-w-2xl font-serif text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
          {title}
        </h2>
      </Reveal>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.08}>
            <div className="group h-full rounded-2xl border border-slate-200 bg-cream p-6 transition-all duration-300 hover:-translate-y-1 hover:border-salmon/40 hover:shadow-xl">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-salmon-light text-salmon transition-colors group-hover:bg-salmon group-hover:text-white">
                <w.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-sans text-lg font-semibold text-navy">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const STEPS = [
  { n: "01", title: "Free In-Home Consultation", text: "We visit, measure, listen, and hand you a written line-item estimate — free, with zero pressure." },
  { n: "02", title: "Design & Material Selection", text: "Together we choose tile, fixtures and finishes at local showrooms, then lock your schedule." },
  { n: "03", title: "Master Craftsmanship & Clean Finish", text: "One dedicated crew builds your project, protects your home daily, and walks it with you at the end." },
];

export const ProcessSteps = () => (
  <section className="bg-cream py-16 md:py-24" data-testid="process-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Overline>How It Works</Overline>
        <h2 className="mt-3 font-serif text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
          Three Steps to a Home You Love
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-10 md:grid-cols-3">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.12} className="relative">
            <p className="font-serif text-6xl font-bold text-salmon/25">{s.n}</p>
            <h3 className="mt-3 font-sans text-lg font-semibold text-navy">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
            {i < 2 && <span className="absolute -right-5 top-8 hidden h-px w-10 bg-salmon/40 md:block" />}
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const CTABanner = ({ title = "Ready to love your home again?" }) => (
  <section className="bg-salmon py-12 md:py-16" data-testid="cta-banner">
    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 text-center md:flex-row md:text-left lg:px-10">
      <div>
        <h2 className="font-serif text-2xl font-bold text-white sm:text-3xl">{title}</h2>
        <p className="mt-1 text-sm text-white/85">Free in-home estimates across Huntsville, Madison & Athens.</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <a
          href={COMPANY.phoneHref}
          data-testid="cta-banner-phone-button"
          className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-salmon-dark shadow-md transition-all hover:shadow-lg"
        >
          <Phone className="h-4 w-4" /> {COMPANY.phone}
        </a>
        <Link
          to="/contact"
          data-testid="cta-banner-estimate-button"
          className="flex items-center gap-2 rounded-full border-2 border-white px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white hover:text-salmon-dark"
        >
          Get Free Estimate <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);

export const FinalCTA = ({ heading = "Get Your Free Remodeling Estimate", sub = "Tell us about your project and we'll call you back within one business day — with a real number, not a sales pitch." }) => (
  <section className="bg-navy py-16 md:py-24" data-testid="final-cta-section">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:px-10">
      <Reveal>
        <Overline light>Free · No Obligation · Local</Overline>
        <h2 className="mt-3 font-serif text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
          {heading}
        </h2>
        <p className="mt-4 max-w-md text-base leading-relaxed text-white/70">{sub}</p>
        <a
          href={COMPANY.phoneHref}
          data-testid="final-cta-phone-link"
          className="mt-8 inline-flex items-center gap-3 font-serif text-3xl font-bold text-salmon-vibrant transition-colors hover:text-white sm:text-4xl"
        >
          <Phone className="h-7 w-7" /> {COMPANY.phone}
        </a>
        <p className="mt-3 text-sm text-white/50">{COMPANY.hours}</p>
      </Reveal>
      <Reveal delay={0.15}>
        <EstimateForm
          idPrefix="final-cta"
          title="Request Your Free Estimate"
          full
          source="final-cta"
        />
      </Reveal>
    </div>
  </section>
);
